import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import Slider from 'react-slick';
import EmbeddedVideo from 'src/components/EmbeddedVideo';
import classNames from 'classnames';
import ImageWithFallback from 'src/components/ImageWithFallback';
import Player from 'src/lib/video_player';
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import style from 'src/styles/modules/components/lightbox.module.scss';
import { range } from 'lodash-es';

export type LightBoxItem = {
  id: string | number;
  videoId?: string;
  imageUrl?: string;
  smImageUrl?: string;
  caption?: string;
};

type Props = {
  show?: boolean;
  items: LightBoxItem[];
  enableNav: boolean;
  onClose: () => void;
  onShow?: () => void;
  slideId?: string;
};

const LightBox: React.FC<Props> = ({
  show = false,
  enableNav = true,
  slideId,
  onClose,
  onShow,
  items,
}) => {
  const lightboxRef = useRef<HTMLDivElement>(null);
  const mainSlider = useRef<Slider>(null);
  const navSlider = useRef<Slider>(null);
  const [isMainSliderMounted, setIsMainSliderMounted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  let prevSlide = currentSlide - 1;
  let nextSlide = currentSlide + 1;

  const pauseCurrentVideo = () => {
    if (mainSlider?.current?.innerSlider?.list) {
      const iframe = mainSlider.current.innerSlider.list.querySelector(
        '.slick-active.slick-current .js-video-embed'
      );
      if (iframe instanceof HTMLIFrameElement) {
        new Player(iframe, {
          onReady: (player) => {
            player.pause()
          }
        });
      }
    }
  };

  useEffect(() => {
    if (show) {
      typeof onShow === 'function' && onShow();
      if (lightboxRef.current) {
        disableBodyScroll(lightboxRef.current)
      }
    } else {
      if (lightboxRef.current) {
        enableBodyScroll(lightboxRef.current)
      }
    }
  }, [show, onShow]);

  useEffect(() => {
    if (mainSlider.current) {
      setIsMainSliderMounted(true);
    }
  }, [JSON.stringify(items)]);

  useEffect(() => {
    if (!isMainSliderMounted) return;
    let slideEle: HTMLElement | null = document.querySelector(`#${slideId}`);
    slideEle = slideEle ? slideEle.closest('.slick-slide') : null;
    if (slideEle) {
      const slideIndex = slideEle.dataset.index;
      if (slideIndex) {
        if (navSlider.current) {
          navSlider.current.slickGoTo(Number(slideIndex));
          setCurrentSlide(Number(slideIndex));
        }
      }
    }
  }, [slideId, isMainSliderMounted]);

  if (items.length === 0) return <></>;

  return (
    <div ref={lightboxRef} className={classNames('lightbox', { 'tw-hidden': !show })}>
      <div className="lightbox__inner">
        <div
          className="lightbox__btn-close"
          onClick={() => {
            pauseCurrentVideo();
            typeof onClose === 'function' && onClose();
          }}
        >
          <FiX size={48} strokeWidth={1} />
        </div>

        <div
          className={classNames('lightbox__btn-next slick-arrow')}
          onClick={() => {
            if (mainSlider.current) {
              pauseCurrentVideo();
              nextSlide = nextSlide >= items.length ? 0 : nextSlide;
              mainSlider.current.slickGoTo(nextSlide);
            }
          }}
        >
          <FiChevronRight size={24} strokeWidth={1} />
        </div>

        <div
          className={classNames('lightbox__btn-prev slick-arrow')}
          onClick={() => {
            if (mainSlider.current && mainSlider.current.innerSlider) {
              pauseCurrentVideo();
              prevSlide = prevSlide < 0 ? items.length - 1 : prevSlide;
              mainSlider.current.slickGoTo(prevSlide);
            }
          }}
        >
          <FiChevronLeft size={24} strokeWidth={1} />
        </div>

        <div className={classNames('lightbox__main', {[style.lgLightbox]: !enableNav})}>
          <div className="lightbox__main-carousel tw-h-full">
            <Slider
              ref={mainSlider}
              asNavFor={navSlider.current || undefined}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              fade={true}
              beforeChange={(_, currentSlide) => setCurrentSlide(currentSlide)}
            >
              {items.map((item) => (
                <div
                  key={item.id}
                  className="lightbox__main-carousel-item tw-block tw-h-full"
                  id={String(item.id)}
                >
                  <div className="lightbox__main-carousel-item-inner tw-h-full">
                    {item.videoId ? (
                      <div className={classNames('lightbox__main-carousel-item-img tw-h-full', {'md:tw-h-96': enableNav})}>
                        <div className="lightbox-video-container">
                          <EmbeddedVideo videoConfig={{ controls: 1 }} videoId={item.videoId} />
                        </div>
                      </div>
                    ) : item.imageUrl ? (
                      <div className="tw-flex tw-flex-col tw-justify-center tw-h-full">
                        <ImageWithFallback
                          src={item.imageUrl}
                          className={classNames(
                            'tw-object-cover tw-static tw-cursor-pointer md:tw-w-auto tw-w-full tw-h-auto tw-max-w-full tw-max-h-full tw-mx-auto tw-border tw-border-brand-green tw-border-solid tw-rounded-lg',
                            { 'md:tw-h-96': enableNav }
                          )}
                          fill
                          alt={item?.caption || ''}
                          unoptimized={true}
                        />
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>

        <div className="lightbox__nav">
          <div className="tw-text-center tw-mb-4">
            <div>
              <span className="tw-text-white tw-font-semibold">{currentSlide + 1}</span>{' '}
              <span className="tw-text-white tw-font-semibold">/ {items.length}</span>
            </div>
          </div>
          <div
            className={classNames('lightbox__nav-carousel', {
              'tw-invisible': !enableNav || !isMainSliderMounted,
            })}
          >
            <Slider
              ref={navSlider}
              asNavFor={mainSlider.current as Slider}
              slidesToShow={4}
              slidesToScroll={1}
              focusOnSelect={true}
              infinite={false}
              arrows={false}
            >
              {items.map((item) => {
                const imgUrl = item.smImageUrl || item.imageUrl;
                return (
                  <div key={item.id} className="lightbox__nav-carousel-item">
                    <div className="lightbox__nav-carousel-item-img">
                      {item.videoId ? (
                        <div className="position-relative">
                          <EmbeddedVideo videoId={item.videoId} />
                          <div className="video-overlay"></div>
                        </div>
                      ) : imgUrl ? (
                        <ImageWithFallback
                          src={imgUrl}
                          fill
                          className="tw-object-cover tw-static tw-block tw-rounded-md"
                          alt={item.caption || ''}
                          unoptimized={true}
                        />
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                );
              })}
              {items.length < 4 ? range(4 - items.length).map((k) => (
                <div key={k}></div>
              )) : null}
            </Slider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LightBox;
